import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { ITheme } from "../../../../Themes/ThemeTypes";
import { themeColor } from "../../../../Themes/ThemeFunctions";
import { ThemeColorEnum } from "../../../../Themes/ThemeEnums";
import {
  SideSheetSlotContext,
  type SideSheetSlotContextValue,
} from "../../../../SideSheet/SideSheetSlotContext";
import { LocalStorageKeyEnum } from "../../../../../../common/enums/LocalStorageKeyEnum";

const SLOT_DEFAULT_WIDTH_PX = 400;
const SLOT_MIN_WIDTH_PX = 280;
const SLOT_MAX_WIDTH_VW_FRACTION = 0.9;

const Row = styled.div<{ theme: ITheme; $isFullscreen?: boolean }>`
  display: flex;
  width: 100%;
  height: ${(props) => (props.$isFullscreen ? "100%" : "calc(100% - 64px)")};
  flex-direction: row;
  overflow: hidden;
  background: ${(props) => themeColor(ThemeColorEnum.SURFACE_VARIANT, props.theme)};
`;

const Slot = styled.div<{
  theme: ITheme;
  $open: boolean;
  $widthPx: number;
  $isDragging: boolean;
  $fullscreen: boolean;
}>`
  flex-shrink: 0;
  overflow: hidden;
  ${(props) =>
    props.$fullscreen
      ? `
        position: fixed;
        inset: 0;
        width: 100vw;
        z-index: ${props.theme.base.z_index.overlay};
      `
      : `
        position: relative;
        width: ${props.$open ? `min(${props.$widthPx}px, 90vw)` : "0"};
      `}
  /* Disable transition while user drags to avoid lag between cursor and slot edge. */
  transition: ${(props) =>
    props.$isDragging
      ? "none"
      : `width ${props.theme.base.motion.duration.long2}ms ${props.theme.base.motion.easing.standard_decelerate}`};
`;

const ResizeHandle = styled.div<{ theme: ITheme }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 16px;
  cursor: col-resize;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  z-index: 1;
`;

// M3 VerticalDragHandle: outline pill, 4x48dp at rest, growing to 12x52dp and
// darkening to on_surface on hover/drag.
const ResizeHandleBar = styled.div<{ theme: ITheme }>`
  width: 4px;
  height: 48px;
  border-radius: 9999px;
  background: rgb(${(props) => props.theme.colors.outline});
  transition:
    width ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard},
    height ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard},
    background-color ${(props) => props.theme.base.motion.duration.short4}ms
      ${(props) => props.theme.base.motion.easing.standard};

  ${ResizeHandle}:hover &,
  ${ResizeHandle}:active & {
    width: 12px;
    height: 52px;
    background: rgb(${(props) => props.theme.colors.on_surface});
  }
`;

const readStoredWidth = (): number => {
  if (typeof window === "undefined") return SLOT_DEFAULT_WIDTH_PX;
  const stored = window.localStorage.getItem(LocalStorageKeyEnum.SIDE_SHEET_SLOT_WIDTH);
  const parsed = stored ? parseInt(stored, 10) : NaN;
  if (Number.isFinite(parsed) && parsed >= SLOT_MIN_WIDTH_PX) {
    return parsed;
  }
  return SLOT_DEFAULT_WIDTH_PX;
};

interface AppBodyProps {
  children: React.ReactNode;
  $isFullscreen?: boolean;
}

const AppBody: React.FC<AppBodyProps> = ({ children, $isFullscreen }) => {
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const [standardOpen, setStandardOpen] = useState(false);
  const [fullscreenRequested, setFullscreenRequested] = useState(false);
  const [slotWidth, setSlotWidth] = useState<number>(readStoredWidth);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const slotFullscreen = standardOpen && fullscreenRequested;

  const openSlot = useCallback((open: boolean) => {
    setStandardOpen(open);
    if (!open) setFullscreenRequested(false);
  }, []);

  const ctxValue = useMemo<SideSheetSlotContextValue>(
    () => ({
      slot,
      setStandardOpen: openSlot,
      fullscreen: slotFullscreen,
      setFullscreen: setFullscreenRequested,
    }),
    [slot, slotFullscreen, openSlot],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragStateRef.current = { startX: e.clientX, startWidth: slotWidth };
      setIsDragging(true);
    },
    [slotWidth],
  );

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMove = (e: MouseEvent): void => {
      const ds = dragStateRef.current;
      if (!ds) return;
      // Slot is at the right edge; widening = dragging left (deltaX < 0).
      const deltaX = ds.startX - e.clientX;
      const ceiling = window.innerWidth * SLOT_MAX_WIDTH_VW_FRACTION;
      const next = Math.min(ceiling, Math.max(SLOT_MIN_WIDTH_PX, ds.startWidth + deltaX));
      setSlotWidth(next);
    };

    const handleUp = (): void => {
      setIsDragging(false);
      dragStateRef.current = null;
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
    return () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };
  }, [isDragging]);

  // Persist resized width to localStorage when drag ends.
  useEffect(() => {
    if (isDragging || typeof window === "undefined") return;
    window.localStorage.setItem(LocalStorageKeyEnum.SIDE_SHEET_SLOT_WIDTH, String(slotWidth));
  }, [isDragging, slotWidth]);

  return (
    <SideSheetSlotContext.Provider value={ctxValue}>
      <Row $isFullscreen={$isFullscreen}>
        {children}
        <Slot
          ref={setSlot}
          $open={standardOpen}
          $widthPx={slotWidth}
          $isDragging={isDragging}
          $fullscreen={slotFullscreen}
        >
          {standardOpen && !slotFullscreen && (
            <ResizeHandle
              onMouseDown={handleMouseDown}
              role="separator"
              aria-orientation="vertical"
            >
              <ResizeHandleBar />
            </ResizeHandle>
          )}
        </Slot>
      </Row>
    </SideSheetSlotContext.Provider>
  );
};

export default AppBody;
